import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';

import List, {ListItem} from 'app/components/List';
import {TagSetting} from 'app/modules/Remote/Organization/types';
import colors from 'app/styles/colors.json';
import * as tagUtils from 'app/utils/tagUtils';

import cs from './PropertyOverview.module.scss';

export type ImageryFilterValue =
  | 'all'
  | 'purchased'
  | 'not-purchased'
  | 'not-purchased-submeter'
  | 'none';

export type NoteCountFilterValue = null | '>0' | '0';

export interface TagFilterValue {
  op: 'any' | 'all' | 'none';
  tagIds: I.Set<string>;
}

export function encodeTagFilterValue(val: TagFilterValue) {
  return `${val.op}:${val.tagIds.join(',')}`;
}

/**
 * The filter value for tags is the operation followed by a colon, then a
 * comma-separated list of tag IDs.
 */
export function decodeTagFilterValue(
  val: string,
  tagSettings: I.OrderedMap<string, I.ImmutableOf<TagSetting>>
): TagFilterValue {
  const bits = val?.split(':');
  if (bits?.length !== 2) {
    return {op: 'all', tagIds: I.Set([])};
  } else {
    const tagIds = bits[1]
      .split(',')
      // Remove the single blank you get when calling split on ""
      .filter((s) => !!s);

    // If any of the active tag IDs are invalid, update the filter state with
    // only the valid tag IDs.
    const validTagIds = tagIds.filter((id) => tagSettings.find((s) => s!.get('id') === id));

    return {
      op: bits[0] as TagFilterValue['op'],
      tagIds: I.Set(validTagIds),
    };
  }
}

export const TagFilterContent: React.FunctionComponent<
  React.PropsWithChildren<{
    tagSettings: I.OrderedMap<string, I.ImmutableOf<TagSetting>>;
    value: TagFilterValue;
    setValue: (v: TagFilterValue | undefined) => void;
  }>
> = ({tagSettings, value, setValue}) => {
  return (
    <div>
      <div className={cs.tagFilterOptions}>
        <div>Show properties with</div>

        <B.ButtonGroup fill>
          {(['any', 'all', 'none'] as const).map((op) => (
            <B.Button
              key={op}
              text={op}
              onClick={(ev: React.MouseEvent) => {
                ev.preventDefault();
                setValue({...value, op});
              }}
              active={value.op === op}
              outlined
              small
            />
          ))}
        </B.ButtonGroup>

        <div>of the following tags:</div>
      </div>

      <List
        className={cs.tagFilterWrapper}
        items={[
          ...tagUtils.makeTagListItems(tagSettings, (tagId) => value.tagIds.includes(tagId)),
          {id: 'sep1', text: '', isSeparator: true},
          {id: 'reset', text: 'Reset filter'},
        ]}
        onClickItem={({id}, ev) => {
          ev.preventDefault();

          if (id === 'reset') {
            setValue(undefined);
          } else {
            const isSelected = value.tagIds.includes(id);

            if (isSelected) {
              setValue({...value, tagIds: value.tagIds.remove(id)});
            } else {
              setValue({...value, tagIds: value.tagIds.add(id)});
            }
          }
        }}
      />
    </div>
  );
};

export function mapAllAnyNoneMenuIdToFilterValue(id: string): string | undefined {
  if (id === 'all') return undefined;
  if (id === 'any') return '*';
  if (id === 'none') return '';
  return id;
}

export function mapNoteCountMenuIdToFilterValue(id: string): string | undefined {
  if (id === 'all') return undefined;
  if (id === 'some') return '>0';
  if (id === 'none') return '0';
  return id;
}

export function imageryFilterListItems(
  value: ImageryFilterValue,
  isHiddenImageryShownAsAvailable: boolean
): ListItem<{id: string}>[] {
  return [
    {id: 'all', text: 'All', isSelected: value === 'all'},
    {id: 'sep', text: '', isSeparator: true},
    {id: 'purchased', text: 'Ordered', isSelected: value === 'purchased'},
    {id: 'not-purchased', text: 'Unordered all', isSelected: value === 'not-purchased'},
    {
      id: 'not-purchased-submeter',
      text: 'Unordered sub-meter',
      isSelected: value === 'not-purchased-submeter',
    },
    {id: 'sep2', text: '', isSeparator: true},
    {id: 'none', text: 'No Imagery', isSelected: value === 'none'},
    {id: 'sep3', text: '', isSeparator: true},
    {
      id: 'hidden',
      text: 'Show Hidden as Available',
      icon: isHiddenImageryShownAsAvailable ? 'small-tick' : 'blank',
    },
  ] as ListItem<{id: string}>[];
}

export function noteCountFilterListItems(value: NoteCountFilterValue): ListItem<{id: string}>[] {
  return [
    {id: 'all', text: 'All', isSelected: value === null},
    {id: 'sep', text: '', isSeparator: true},
    {id: 'some', text: 'Has Some', isSelected: value === '>0'},
    {id: 'none', text: 'Has None', isSelected: value === '0'},
  ];
}

export function dateFilterListItems(value: string | null): ListItem<{id: string}>[] {
  return [
    {id: 'all', text: 'All', isSelected: value === null},
    {id: 'sep', text: '', isSeparator: true},
    {id: 'any', text: 'Any Date', isSelected: value === '*'},
    {id: 'none', text: 'No Date', isSelected: value === ''},
    {id: 'sep2', text: '', isSeparator: true},
    {id: '7', text: 'Next 7 days', isSelected: value === '7'},
    {id: '30', text: 'Next 30 days', isSelected: value === '30'},
    {id: '90', text: 'Next 90 days', isSelected: value === '90'},
  ];
}

export function assigneeFilterBaseListItems(value: string | null): ListItem<{id: string}>[] {
  return [
    {id: 'all', text: 'All Properties', isSelected: value === null},
    {id: 'sep', text: '', isSeparator: true},
    {id: 'any', text: 'Any Assignee', isSelected: value === '*'},
    {id: 'none', text: 'No Assignee', isSelected: value === ''},
    {id: 'non-user', text: 'Assigned to Non-user', isSelected: value === 'non-user'},
    {id: 'sep2', text: '', isSeparator: true},
  ];
}

const PropertyFilterOptions = ['any', 'none', 'contains'] as const;
export type PropertyFilterOptions = (typeof PropertyFilterOptions)[number];

export function isPropertyOverviewFilterActive(args: {
  columnId: string;
  rawValue: unknown;
  tagSettings?: I.OrderedMap<string, I.ImmutableOf<TagSetting>>;
  additionalFeaturePropertyColumnIds?: I.Set<string>;
}): boolean {
  const {columnId, rawValue, tagSettings, additionalFeaturePropertyColumnIds} = args;

  if (rawValue === undefined || rawValue === null) return false;

  if (additionalFeaturePropertyColumnIds?.has(columnId)) {
    return typeof rawValue === 'string' && rawValue.length > 0;
  }

  // Empty strings count as active for some filters.
  if (rawValue === '') return true;

  if (columnId === 'tags' && typeof rawValue === 'string' && tagSettings) {
    const decoded = decodeTagFilterValue(rawValue, tagSettings);
    return !decoded.tagIds.isEmpty();
  }

  if (columnId === 'imagery') {
    return rawValue !== 'all';
  }

  return true;
}

export function countActivePropertyOverviewFilters(args: {
  filters: Array<{id: string; value: unknown}>;
  tagSettings?: I.OrderedMap<string, I.ImmutableOf<TagSetting>>;
  additionalFeaturePropertyColumnIds?: I.Set<string>;
}): number {
  const {filters, tagSettings, additionalFeaturePropertyColumnIds} = args;
  return filters.filter((f) =>
    isPropertyOverviewFilterActive({
      columnId: f.id,
      rawValue: f.value,
      tagSettings,
      additionalFeaturePropertyColumnIds,
    })
  ).length;
}

export function shouldResetTagFilter(v: TagFilterValue | undefined): boolean {
  return !v || (v.tagIds.isEmpty() && v.op === 'all');
}

export const filterIcon = <B.Icon icon="filter" color={colors.darkestGray} />;
